import { CircularProgress, Input, Table, TableCell, TableHead, TablePagination, TableRow } from '@mui/material';
import { FC, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { ItemType, UploadType } from 'api/Responses/BulkUploadResponses';
import EmptyState from 'components/EmptyState/EmptyState';
import { useLanguage } from 'context/AppContext';
import { AppDispatch } from 'store';
import dataUploadSlice, { selectIgnore, selectUnique, selectUpdate } from 'store/dataUpload/dataUploadSlice';
import { DataUploadCheckStatus } from '../DataUpload.helpers';
import ApproveAllModal from './ApproveAllModal';
import * as Styles from './DataUploadMergeTable.styles';
import UpdateDeviceListItem from './UpdateDeviceListItem';

interface DataUploadMergeTableParams {
  uploadType: UploadType;
  checkStatus: DataUploadCheckStatus;
  uploadId: string;
  isLoading: boolean;
}

const uniqueDeviceHeaders = ['Device Code', 'Device Name', 'Number of Parts', 'Ignore Upload'];
const updateDeviceHeaders = ['Device Code', 'Device Name', 'Number of Parts', 'Investigate', 'New Draft'];
const ignoreDeviceHeaders = ['Device Code', 'Device Name', 'Number of Parts', 'Reason', 'Restore Data?'];

const uniqueSetHeaders = ['Set Code', 'Set Name', 'Number of Trays', 'Number of Devices', 'Ignore Upload'];
const updateSetHeaders = ['Set Code', 'Set Name', 'Number of Trays', 'Number of Devices', 'Investigate', 'New Draft'];
const ignoreSetHeaders = ['Set Code', 'Set Name', 'Number of Trays', 'Number of Devices', 'Reason', 'Restore Data?'];

const uniqueSetAttachmentHeaders = ['Set Code', 'Set Attachment Name', 'Ignore Attachment Upload'];
const updateSetAttachmentHeaders = ['Set Code', 'Set Attachment Name', 'Investigate', 'New Draft'];
const ignoreSetAttachmentHeaders = ['Set Code', 'Set Attachmnet Name', 'Reason', 'Restore Data?'];

const uniqueDeviceAttachmentHeaders = ['Device Code', 'Device Attachment Name', 'Ignore Attachment Upload'];
const updateDeviceAttachmentHeaders = ['Device Code', 'Device Attachment Name', 'Investigate', 'New Draft'];
const ignoreDeviceAttachmentHeaders = ['Device Code', 'Device Attachment Name', 'Reason', 'Restore Data?'];

const uniqueSetSerialNumbersHeaders = ['Set Code', 'Set Name', 'Serial Number', 'Ignore Upload'];
const updateSetSerialNumbersHeaders = ['Set Code', 'Set Name', 'Serial Number', 'Investigate', 'New Draft'];
const ignoreSetSerialNumbersHeaders = ['Set Code', 'Set Name', 'Serial Number', 'Reason', 'Restore Data?'];

const filterData = ({ value, fields }: { value: string; fields: Array<string | undefined> }) =>
  fields.some(field => field && field.toLowerCase().includes(value.toLowerCase()));

const DataUploadMergeTable: FC<DataUploadMergeTableParams> = ({ uploadType, checkStatus, uploadId, isLoading }) => {
  const [searchInput, setSearchInput] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [approveAllModalOpen, setApproveAllModalOpen] = useState(false);
  const [language] = useLanguage();
  const [currentTabData, setCurrentTabData] = useState<ItemType[]>([]);
  const dispatch = useDispatch<AppDispatch>();
  const [filteredValues, setFilteredValues] = useState<ItemType[]>([]);

  const uniqueArray = useSelector(selectUnique);
  const ignoreArray = useSelector(selectIgnore);
  const updateArray = useSelector(selectUpdate);

  const tableHeaders = useMemo(() => {
    switch (true) {
      case uploadType === UploadType['Set Upload'] && checkStatus === DataUploadCheckStatus.UNIQUE:
        return uniqueSetHeaders;
      case uploadType === UploadType['Set Attachments'] && checkStatus === DataUploadCheckStatus.UNIQUE:
        return uniqueSetAttachmentHeaders;
      case uploadType === UploadType['Device Upload'] && checkStatus === DataUploadCheckStatus.UNIQUE:
        return uniqueDeviceHeaders;
      case uploadType === UploadType['Device Attachments'] && checkStatus === DataUploadCheckStatus.UNIQUE:
        return uniqueDeviceAttachmentHeaders;
      case uploadType === UploadType['Set Serial Numbers'] && checkStatus === DataUploadCheckStatus.UNIQUE:
        return uniqueSetSerialNumbersHeaders;
      case uploadType === UploadType['Set Upload'] && checkStatus === DataUploadCheckStatus.IGNORE:
        return ignoreSetHeaders;
      case uploadType === UploadType['Set Attachments'] && checkStatus === DataUploadCheckStatus.IGNORE:
        return ignoreSetAttachmentHeaders;
      case uploadType === UploadType['Device Upload'] && checkStatus === DataUploadCheckStatus.IGNORE:
        return ignoreDeviceHeaders;
      case uploadType === UploadType['Device Attachments'] && checkStatus === DataUploadCheckStatus.IGNORE:
        return ignoreDeviceAttachmentHeaders;
      case uploadType === UploadType['Set Serial Numbers'] && checkStatus === DataUploadCheckStatus.IGNORE:
        return ignoreSetSerialNumbersHeaders;
      case uploadType === UploadType['Set Upload'] && checkStatus === DataUploadCheckStatus.UPDATE:
        return updateSetHeaders;
      case uploadType === UploadType['Set Attachments'] && checkStatus === DataUploadCheckStatus.UPDATE:
        return updateSetAttachmentHeaders;
      case uploadType === UploadType['Device Upload'] && checkStatus === DataUploadCheckStatus.UPDATE:
        return updateDeviceHeaders;
      case uploadType === UploadType['Device Attachments'] && checkStatus === DataUploadCheckStatus.UPDATE:
        return updateDeviceAttachmentHeaders;
      case uploadType === UploadType['Set Serial Numbers'] && checkStatus === DataUploadCheckStatus.UPDATE:
        return updateSetSerialNumbersHeaders;
      default:
        return undefined;
    }
  }, [uploadType, checkStatus]);

  useEffect(() => {
    if (checkStatus === DataUploadCheckStatus.UNIQUE) {
      setCurrentTabData(uniqueArray);
    } else if (checkStatus === DataUploadCheckStatus.IGNORE) {
      setCurrentTabData(ignoreArray);
    } else if (checkStatus === DataUploadCheckStatus.UPDATE) {
      setCurrentTabData(updateArray);
    }
  }, [checkStatus, uniqueArray, updateArray, ignoreArray]);

  useEffect(() => {
    if (uploadType === UploadType['Set Upload']) {
      if (checkStatus === DataUploadCheckStatus.UPDATE) {
        const filteredResponse = searchInput
          ? currentTabData.filter(item => {
              return filterData({
                value: searchInput,
                fields: [item?.updated_data?.description, item?.updated_data?.set_code],
              });
            })
          : currentTabData;
        setFilteredValues(filteredResponse);
      } else {
        const filteredResponse = searchInput
          ? currentTabData.filter(item => {
              return filterData({
                value: searchInput,
                fields: [item?.description, item?.set_code],
              });
            })
          : currentTabData;
        setFilteredValues(filteredResponse);
      }
    } else if (uploadType === UploadType['Device Upload']) {
      if (checkStatus === DataUploadCheckStatus.UPDATE) {
        const filteredResponse = searchInput
          ? currentTabData.filter(item => {
              return filterData({
                value: searchInput,
                fields: [item?.updated_data?.description, item?.updated_data?.device_id],
              });
            })
          : currentTabData;
        setFilteredValues(filteredResponse);
      } else {
        const filteredResponse = searchInput
          ? currentTabData.filter(item => {
              return filterData({
                value: searchInput,
                fields: [item?.description, item?.device_id],
              });
            })
          : currentTabData;
        setFilteredValues(filteredResponse);
      }
    } else if (uploadType === UploadType['Device Attachments']) {
      if (checkStatus === DataUploadCheckStatus.UPDATE) {
        const filteredResponse = searchInput
          ? currentTabData.filter(item => {
              return filterData({
                value: searchInput,
                fields: [item?.updated_data?.file_path, item?.updated_data?.device_id],
              });
            })
          : currentTabData;
        setFilteredValues(filteredResponse);
      } else {
        const filteredResponse = searchInput
          ? currentTabData.filter(item => {
              return filterData({
                value: searchInput,
                fields: [item?.file_path, item?.device_id],
              });
            })
          : currentTabData;
        setFilteredValues(filteredResponse);
      }
    } else if (uploadType === UploadType['Set Attachments']) {
      if (checkStatus === DataUploadCheckStatus.UPDATE) {
        const filteredResponse = searchInput
          ? currentTabData.filter(item => {
              return filterData({
                value: searchInput,
                fields: [item?.updated_data?.file_path, item?.updated_data?.set_code],
              });
            })
          : currentTabData;
        setFilteredValues(filteredResponse);
      } else {
        const filteredResponse = searchInput
          ? currentTabData.filter(item => {
              return filterData({
                value: searchInput,
                fields: [item?.file_path, item?.set_code],
              });
            })
          : currentTabData;
        setFilteredValues(filteredResponse);
      }
    } else if (uploadType === UploadType['Set Serial Numbers']) {
      if (checkStatus === DataUploadCheckStatus.UPDATE) {
        const filteredResponse = searchInput
          ? currentTabData.filter(item => {
              return filterData({
                value: searchInput,
                fields: [item?.updated_data?.set_code, item.updated_data?.set_uss],
              });
            })
          : currentTabData;
        setFilteredValues(filteredResponse);
      } else {
        const filteredResponse = searchInput
          ? currentTabData.filter(item => {
              return filterData({
                value: searchInput,
                fields: [item?.set_code, item.set_uss],
              });
            })
          : currentTabData;
        setFilteredValues(filteredResponse);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchInput, currentTabData]);

  const handleIgnoreItem = (item: ItemType) => {
    dispatch(dataUploadSlice.actions.ignoreItem(item));
  };

  const handleRestoreItem = (item: ItemType) => {
    if (item.updateItem) {
      dispatch(dataUploadSlice.actions.restoreDuplicate(item));
    } else if (item.uniqueItem) {
      dispatch(dataUploadSlice.actions.restoreItem(item));
    }
  };

  const handleApproveDuplicate = (item: ItemType) => {
    dispatch(dataUploadSlice.actions.addDuplicate(item));
  };

  const handleIgnoreDuplicate = (item: ItemType) => {
    if (uploadType === UploadType['Set Attachments'] || uploadType === UploadType['Device Attachments']) {
      dispatch(dataUploadSlice.actions.ignoreDuplicateSetOrDevice(item));
    } else if (
      uploadType === UploadType['Set Upload'] ||
      uploadType === UploadType['Device Upload'] ||
      uploadType === UploadType['Set Serial Numbers']
    ) {
      dispatch(dataUploadSlice.actions.ignoreDuplicateAttachment(item));
    }
  };

  const handleChangePage = (event: React.MouseEvent<HTMLButtonElement> | null, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleSearch = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    event.preventDefault(); // Prevent form submission
    setSearchInput(event.target.value.toLowerCase());
  };

  const clickUpdateAllData = () => {
    setApproveAllModalOpen(true);
  };

  const handleApproveAllClick = () => {
    dispatch(dataUploadSlice.actions.approveAllDuplicates());
  };

  // Avoid a layout jump when reaching the last page with empty rows.
  const emptyRows = useMemo(
    () => (page > 0 ? Math.max(0, (1 + page) * rowsPerPage - filteredValues.length) : 0),
    [page, rowsPerPage, filteredValues.length],
  );

  return (
    <>
      <Styles.ActionBar>
        <Styles.SearchForm>
          <Input placeholder="Search" value={searchInput} onChange={event => handleSearch(event)} autoFocus />
        </Styles.SearchForm>
        {checkStatus === DataUploadCheckStatus.UPDATE ? (
          <Styles.UpdateAllButton variant="outlined" onClick={() => clickUpdateAllData()}>
            {language.DataUpload.dataUploadMergeTable.updateAll}
          </Styles.UpdateAllButton>
        ) : (
          <></>
        )}
      </Styles.ActionBar>
      <Styles.Table>
        {isLoading ? (
          <Styles.Container>
            <CircularProgress />
          </Styles.Container>
        ) : filteredValues && filteredValues.length > 0 ? (
          <>
            <Table stickyHeader>
              <TableHead>
                <TableRow>
                  {tableHeaders &&
                    tableHeaders.map(header => <Styles.HeadTableCell key={header}>{header}</Styles.HeadTableCell>)}
                </TableRow>
              </TableHead>
              <Styles.TableBodyStyled>
                {(rowsPerPage > 0
                  ? filteredValues.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                  : filteredValues
                ).map(item => (
                  <UpdateDeviceListItem
                    item={item}
                    key={item.id}
                    uploadType={uploadType}
                    checkStatus={checkStatus}
                    handleIgnoreItem={handleIgnoreItem}
                    handleRestoreItem={handleRestoreItem}
                    handleApproveDuplicate={handleApproveDuplicate}
                    handleIgnoreDuplicate={handleIgnoreDuplicate}
                  />
                ))}
                {emptyRows > 0 ? (
                  <TableRow style={{ height: 53 * emptyRows }}>
                    <TableCell colSpan={6} />
                  </TableRow>
                ) : null}
              </Styles.TableBodyStyled>
            </Table>
            <Styles.Footer>
              <TableRow>
                <TablePagination
                  rowsPerPageOptions={[10, 25, { label: 'All', value: -1 }]}
                  count={filteredValues.length}
                  rowsPerPage={rowsPerPage}
                  page={page}
                  onPageChange={handleChangePage}
                  onRowsPerPageChange={handleChangeRowsPerPage}
                />
              </TableRow>
            </Styles.Footer>
          </>
        ) : (
          <EmptyState />
        )}
      </Styles.Table>
      <ApproveAllModal
        open={approveAllModalOpen}
        close={() => setApproveAllModalOpen(false)}
        handleApproveAllClick={handleApproveAllClick}
      />
    </>
  );
};

export default DataUploadMergeTable;
